import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Upload,
  Save,
  ImageOff,
  LayoutDashboard,
  PackagePlus,
  ArrowLeft,
  Lock,
  LogOut,
  User,
  Package,
  ClipboardList,
  Phone,
  MapPin,
  Eye,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { formatRupiah } from "../mock";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getOrders,
  deleteOrder,
} from "../api";

// Demo admin credentials
const ADMIN_USER = "admin";
const ADMIN_PASS = "ruangrasa2026";

const empty = {
  name: "",
  category: "Wall Art",
  description: "",
  price: 0,
  oldPrice: 0,
  badge: "Baru",
  color: "#DFF0EA",
  variants: [],
  images: [],
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Admin = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(
    () => localStorage.getItem("rr_admin_auth") === "1"
  );
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [loginErr, setLoginErr] = useState("");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("produk"); // produk | orderan
  const [proofView, setProofView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=new, obj=edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleLogin = (e) => {
    e?.preventDefault();
    if (
      creds.username.trim() === ADMIN_USER &&
      creds.password === ADMIN_PASS
    ) {
      localStorage.setItem("rr_admin_auth", "1");
      setAuthed(true);
      setLoginErr("");
      toast({ title: "Berhasil masuk!", description: "Selamat datang, Admin." });
    } else {
      setLoginErr("Username atau password salah.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("rr_admin_auth");
    setAuthed(false);
    setCreds({ username: "", password: "" });
  };

  const load = () => {
    setLoading(true);
    getProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };

  const loadOrders = () => {
    getOrders()
      .then(setOrders)
      .catch(() => setOrders([]));
  };

  useEffect(() => {
    if (authed) {
      load();
      loadOrders();
    }
  }, [authed]);

  const handleDeleteOrder = async (o) => {
    if (!window.confirm(`Hapus order ${o.orderNumber}?`)) return;
    await deleteOrder(o.id);
    toast({ title: "Order dihapus" });
    loadOrders();
  };

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      description: p.description,
      price: p.price,
      oldPrice: p.oldPrice,
      badge: p.badge,
      color: p.color,
      variants: p.variants || [],
      images: p.images || [],
    });
    setModalOpen(true);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 9 - form.images.length;
    if (remaining <= 0) {
      toast({ title: "Maksimal 9 gambar", description: "Hapus dulu untuk menambah." });
      return;
    }
    const toAdd = files.slice(0, remaining);
    const base64s = await Promise.all(toAdd.map(fileToBase64));
    setForm((f) => ({ ...f, images: [...f.images, ...base64s] }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "Nama produk wajib diisi" });
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      oldPrice: Number(form.oldPrice) || 0,
      variants: Array.isArray(form.variants)
        ? form.variants
        : String(form.variants)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
    };
    try {
      if (editing) {
        await updateProduct(editing.id, payload);
        toast({ title: "Produk diperbarui!" });
      } else {
        await createProduct(payload);
        toast({ title: "Produk ditambahkan!" });
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast({ title: "Gagal menyimpan", description: "Coba lagi." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    await deleteProduct(p.id);
    toast({ title: "Produk dihapus" });
    load();
  };

  // ---------------- Login Gate ----------------
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#004743] flex items-center justify-center px-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#0E8A77]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#FD7A18]/15 rounded-full blur-3xl" />

        <div className="relative bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#004743] flex items-center justify-center mb-4">
              <Lock className="w-7 h-7 text-[#FD7A18]" />
            </div>
            <h1 className="text-xl font-extrabold text-[#004743]">
              Login Admin
            </h1>
            <p className="text-sm text-[#6B857E] mt-1">
              Masuk untuk mengelola produk ruangrasa
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-3">
            <div className="relative">
              <User className="w-4 h-4 text-[#8AA59D] absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                className="w-full bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl pl-11 pr-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                placeholder="Username"
                value={creds.username}
                onChange={(e) =>
                  setCreds({ ...creds, username: e.target.value })
                }
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#8AA59D] absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                className="w-full bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl pl-11 pr-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                placeholder="Password"
                value={creds.password}
                onChange={(e) =>
                  setCreds({ ...creds, password: e.target.value })
                }
              />
            </div>

            {loginErr && (
              <p className="text-xs text-red-500 font-semibold text-center">
                {loginErr}
              </p>
            )}

            <button
              type="submit"
              className="w-full bg-[#FD7A18] hover:bg-[#E56A0E] text-white font-bold py-3 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/30"
            >
              Masuk
            </button>
          </form>

          <div className="mt-5 bg-[#FFF1E6] rounded-2xl p-3 text-center">
            <p className="text-[11px] text-[#8a5220] leading-relaxed">
              <span className="font-bold text-[#E8630C]">Demo login:</span>{" "}
              username <b>admin</b> &nbsp;/&nbsp; password <b>ruangrasa2026</b>
            </p>
          </div>

          <Link
            to="/"
            className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-[#4A6B63] hover:text-[#004743] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Toko
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E3F1EC] pb-16">
      {/* Header */}
      <div className="bg-[#004743] pt-10 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Lihat Toko
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-[#FD7A18]" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold text-white">
                  Dashboard Admin
                </h1>
                <p className="text-sm text-white/60">
                  Kelola produk & orderan ruangrasa
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-full font-bold text-sm transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Keluar</span>
              </button>
              {tab === "produk" && (
                <button
                  onClick={openNew}
                  className="inline-flex items-center gap-2 bg-[#FD7A18] hover:bg-[#E56A0E] text-white px-5 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-900/20"
                >
                  <PackagePlus className="w-5 h-5" />
                  Tambah Produk
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setTab("produk")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                tab === "produk"
                  ? "bg-white text-[#004743]"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              <Package className="w-4 h-4" />
              Produk
              <span
                className={`text-[11px] px-1.5 rounded-full ${
                  tab === "produk" ? "bg-[#E3F1ED]" : "bg-white/20"
                }`}
              >
                {products.length}
              </span>
            </button>
            <button
              onClick={() => setTab("orderan")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                tab === "orderan"
                  ? "bg-white text-[#004743]"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              Orderan Pelanggan
              <span
                className={`text-[11px] px-1.5 rounded-full ${
                  tab === "orderan" ? "bg-[#FFF1E6] text-[#E8630C]" : "bg-white/20"
                }`}
              >
                {orders.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Product grid */}
      {tab === "produk" && (
      <div className="max-w-6xl mx-auto px-4 -mt-12">
        {loading ? (
          <div className="bg-white rounded-3xl p-10 text-center text-[#8AA59D] shadow-lg">
            Memuat produk...
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div
                key={p.id}
                className="bg-white rounded-[1.75rem] p-4 shadow-lg shadow-green-900/5"
              >
                <div
                  className="rounded-2xl overflow-hidden aspect-video mb-3 flex items-center justify-center"
                  style={{ backgroundColor: p.color }}
                >
                  {p.images?.[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageOff className="w-8 h-8 text-white/60" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-[#FD7A18] uppercase">
                    {p.category}
                  </span>
                  <span className="text-[10px] text-[#8AA59D]">
                    {p.images?.length || 0}/9 foto
                  </span>
                </div>
                <h3 className="font-bold text-[#004743] text-sm mt-1 line-clamp-1">
                  {p.name}
                </h3>
                <p className="text-base font-extrabold text-[#0B7C6E] mt-1">
                  {formatRupiah(p.price)}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEdit(p)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-[#004743] text-white text-xs font-bold py-2.5 rounded-full hover:bg-[#0A5C53] transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(p)}
                    className="w-10 h-10 flex items-center justify-center bg-[#FCE4EC] text-[#C05561] rounded-full hover:bg-[#f8d0da] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Orders section */}
      {tab === "orderan" && (
        <div className="max-w-6xl mx-auto px-4 -mt-12">
          {orders.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-lg">
              <div className="w-16 h-16 rounded-2xl bg-[#E3F1ED] flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-[#7CC4B4]" />
              </div>
              <p className="font-bold text-[#004743]">Belum Ada Orderan</p>
              <p className="text-sm text-[#8AA59D] mt-1">
                Orderan pelanggan akan muncul di sini setelah checkout.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div
                  key={o.id}
                  className="bg-white rounded-[1.75rem] p-5 shadow-lg shadow-green-900/5"
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-[#004743] flex items-center justify-center shrink-0">
                        <ClipboardList className="w-5 h-5 text-[#FD7A18]" />
                      </div>
                      <div>
                        <p className="font-extrabold text-[#004743]">
                          {o.orderNumber}
                        </p>
                        <p className="text-[11px] text-[#8AA59D]">
                          {formatDate(o.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-[#FFF1E6] text-[#E8630C] text-[11px] font-bold px-3 py-1.5 rounded-full">
                        {o.status || "Menunggu Pembayaran"}
                      </span>
                      <button
                        onClick={() => handleDeleteOrder(o)}
                        className="w-9 h-9 flex items-center justify-center bg-[#FCE4EC] text-[#C05561] rounded-full hover:bg-[#f8d0da] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {/* Buyer + items */}
                    <div>
                      <div className="bg-[#F0F7F5] rounded-2xl p-4">
                        <p className="text-sm font-bold text-[#004743] flex items-center gap-1.5">
                          <User className="w-4 h-4 text-[#0B7C6E]" />
                          {o.buyer?.nama}
                        </p>
                        <p className="text-xs text-[#6B857E] mt-1.5 flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5 shrink-0" />
                          {o.buyer?.hp}
                        </p>
                        <p className="text-xs text-[#6B857E] mt-1 flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          {o.buyer?.alamat}
                        </p>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        {o.items?.map((it, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between text-xs text-[#6B857E]"
                          >
                            <span className="mr-2">
                              {it.name} ({it.variant}) x{it.qty}
                            </span>
                            <span className="font-semibold text-[#004743] shrink-0">
                              {formatRupiah(it.price * it.qty)}
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between border-t border-dashed border-[#CFE7E0] pt-2 mt-1">
                          <span className="text-sm font-bold text-[#004743]">
                            Total
                          </span>
                          <span className="text-sm font-extrabold text-[#0B7C6E]">
                            {formatRupiah(o.total)}
                          </span>
                        </div>
                        {o.note && (
                          <p className="text-[11px] text-[#8AA59D] italic mt-1">
                            Catatan: {o.note}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Payment proof */}
                    <div>
                      <p className="text-xs font-bold text-[#004743] mb-2">
                        Bukti Pembayaran
                      </p>
                      {o.paymentProof ? (
                        <button
                          onClick={() => setProofView(o.paymentProof)}
                          className="relative w-full rounded-2xl overflow-hidden border border-[#CFE7E0] bg-[#F0F7F5] group"
                        >
                          <img
                            src={o.paymentProof}
                            alt="Bukti"
                            className="w-full max-h-44 object-contain"
                          />
                          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                            <span className="opacity-0 group-hover:opacity-100 bg-white text-[#004743] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-opacity">
                              <Eye className="w-3.5 h-3.5" />
                              Lihat
                            </span>
                          </span>
                        </button>
                      ) : (
                        <div className="w-full rounded-2xl border border-dashed border-[#CFE7E0] py-8 flex flex-col items-center justify-center gap-1 text-[#AEC6BE]">
                          <ImageOff className="w-6 h-6" />
                          <span className="text-[11px] font-semibold">
                            Belum ada bukti
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Proof lightbox */}
      {proofView && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-[#004743]/70 backdrop-blur-sm"
          onClick={() => setProofView(null)}
        >
          <button
            onClick={() => setProofView(null)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#004743]"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={proofView}
            alt="Bukti pembayaran"
            className="max-w-full max-h-[85vh] rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-[#004743]/50 backdrop-blur-sm"
            onClick={() => setModalOpen(false)}
          />
          <div className="relative bg-white rounded-[2rem] w-full max-w-2xl max-h-[calc(100vh-3rem)] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-[#E3F1ED] bg-white shrink-0 z-10">
              <h2 className="text-lg font-extrabold text-[#004743]">
                {editing ? "Edit Produk" : "Tambah Produk Baru"}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="w-9 h-9 rounded-full bg-[#F0F7F5] flex items-center justify-center text-[#4A6B63] hover:bg-[#E3F1ED]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Images */}
              <div>
                <label className="text-sm font-bold text-[#004743]">
                  Foto Produk (maks. 9)
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
                  {form.images.map((img, i) => (
                    <div
                      key={i}
                      className="relative aspect-square rounded-2xl overflow-hidden bg-[#E3F1ED] group"
                    >
                      <img
                        src={img}
                        alt={`img-${i}`}
                        className="w-full h-full object-cover"
                      />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 bg-[#FD7A18] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                          Utama
                        </span>
                      )}
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {form.images.length < 9 && (
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="aspect-square rounded-2xl border-2 border-dashed border-[#CFE7E0] flex flex-col items-center justify-center gap-1 text-[#7CC4B4] hover:border-[#FD7A18] hover:text-[#FD7A18] transition-colors"
                    >
                      <Upload className="w-5 h-5" />
                      <span className="text-[10px] font-bold">Upload</span>
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleFiles}
                />
              </div>

              <div>
                <label className="text-sm font-bold text-[#004743]">
                  Judul Produk
                </label>
                <input
                  className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="mis. Cermin Rotan Bulat"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-[#004743]">
                    Kategori
                  </label>
                  <input
                    className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-[#004743]">
                    Badge
                  </label>
                  <input
                    className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-[#004743]">
                    Harga (Rp)
                  </label>
                  <input
                    type="number"
                    className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-[#004743]">
                    Harga Coret (Rp)
                  </label>
                  <input
                    type="number"
                    className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                    value={form.oldPrice}
                    onChange={(e) =>
                      setForm({ ...form, oldPrice: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-[#004743]">
                  Varian (pisahkan dengan koma)
                </label>
                <input
                  className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50"
                  value={
                    Array.isArray(form.variants)
                      ? form.variants.join(", ")
                      : form.variants
                  }
                  onChange={(e) =>
                    setForm({ ...form, variants: e.target.value })
                  }
                  placeholder="mis. 40 cm, 60 cm"
                />
              </div>

              <div>
                <label className="text-sm font-bold text-[#004743]">
                  Deskripsi / Keterangan Produk
                </label>
                <textarea
                  rows={4}
                  className="w-full mt-1.5 bg-[#F0F7F5] border border-[#CFE7E0] rounded-2xl px-4 py-3 text-sm text-[#004743] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50 resize-none"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Tuliskan detail produk, bahan, ukuran, dll."
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-[#E3F1ED] bg-white shrink-0">
              <button
                onClick={() => setModalOpen(false)}
                className="px-6 py-3 rounded-full font-bold text-sm text-[#4A6B63] bg-[#F0F7F5] hover:bg-[#E3F1ED] transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#FD7A18] hover:bg-[#E56A0E] text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 transition-all disabled:opacity-60"
              >
                <Save className="w-5 h-5" />
                {saving ? "Menyimpan..." : "Simpan Produk"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
